/**
 * useAudioAlerts · v2.7.65+E85
 * ─────────────────────────────
 * Operational Audio Notification Engine.
 *
 * Maps WebSocket / DOM custom events to the audio assets already shipped
 * in `/app/frontend/public/`. Mount this hook ONCE per dashboard root
 * (admin / supervisor / dispatcher view) and it handles everything below
 * transparently.
 *
 * Event → audio mapping (v2.7.65+E227 · approved production tones, baked in as
 * the /sounds/ defaults so they ship with every deploy):
 *   rtg:panic_alert      → /sounds/panic-sos-emergency-alert.mp3  (LOOP · until ack)
 *   rtg:public_booking   → /sounds/online-booking-init.mp3       (LOOP · until ack · dispatch/call-in)
 *                        → /sounds/online-booking-alert.mp3      (LOOP · until ack · rider-app when source=rider_account)
 *   rtg:driver_online    → /sounds/driver-online-alert.mp3        (single chime)
 *   rtg:chat_message     → /sounds/chat-message-alert.mp3         (single chime)
 *   rtg:trip_initiated   → /sounds/trip-initiated-alert.mp3       (single chime)
 *
 * Loop behaviour (SOS + Public Booking share the pattern):
 *   - Play in a continuous loop.
 *   - Cannot be silenced by other audio events.
 *   - Listen for `rtg:panic_acknowledged` / `rtg:public_booking_acknowledged`
 *     (or the generic `rtg:acknowledge_all_alarms`) to stop the loop.
 *   - Multiple concurrent alerts keep the loop running until the last is
 *     resolved.
 *
 * Autoplay-policy note:
 *   Browsers block <audio>.play() before the user has interacted with the
 *   page. The AudioActivationOverlay (mounted on the AdminDashboard) writes
 *   `rtg_audio_activated=1` on first tap so subsequent events play cleanly.
 *   The generic `playOnce()` path swallows autoplay rejections silently.
 */
import { useEffect, useRef } from "react";
import adminApi from "@/lib/adminApi";
import * as audioManager from "@/lib/audioManager";
import { subscribeSoundChange } from "@/lib/soundSync";
import { isAudioLeader, onLeadershipChange, activateAudioLeader } from "@/lib/audioLeader";

const FILES = {
  // v2.7.65+E227 · Approved production tones baked in as the DEFAULT assets
  // under /sounds/ (new filenames → cache-busted). These ship with the deploy,
  // so live production plays the approved sound even with no DB override row.
  sos:            "/sounds/panic-sos-emergency-alert.mp3",
  booking:        "/sounds/online-booking-init.mp3",
  // v2.8+E285 · Distinct rider-app booking klaxon (source=rider_account).
  booking_rider:  "/sounds/online-booking-alert.mp3",
  // v2.8+E295 · Distinct URGENT/priority booking klaxon (manual flag or
  // >5-min auto-escalation). Defaults to the normal booking tone until a
  // custom priority sound is uploaded; layers ON TOP of the standard alarms.
  booking_urgent: "/sounds/online-booking-init.mp3",
  // v2.8+E322 · Short one-shot chime for a ride carrying a CRITICAL rider flag
  // (Banned Rider / Possible Violent) landing in the Live Queue. Plays ONCE.
  flag_critical:  "/sounds/driver-dispatch-alert.mp3",
  driver:         "/sounds/driver-online-alert.mp3",
  chat:           "/sounds/chat-message-alert.mp3",
  chat_direct:    "/sounds/chat-message-alert.mp3",   // E108 · fires only for direct (my-session) chats
  trip_initiated: "/sounds/trip-initiated-alert.mp3",
  // v2.7.65+E120 · CORE OPERATIONAL PATCH — Google digital-watch alarm
  // reused across the two newly-audible events (trip finished + ticket
  // pipeline) so ops can distinguish them from the 5 existing tones.
  trip_completed: "https://actions.google.com/sounds/v1/alarms/digital_watch_alarm_long.ogg",
  ticket_pipeline: "https://actions.google.com/sounds/v1/alarms/digital_watch_alarm_long.ogg",
};

// ── v2.8 · Custom system-sound overrides ──────────────────────────────
// Tones uploaded via the Admin Console → Audio Test Panel are stored in
// `system_sound_assignments` and exposed at GET /api/admin/system-sounds.
// The engine loads them on mount AND whenever the panel dispatches
// `rtg:system_sounds_updated`, so a freshly-uploaded tone becomes the ACTIVE
// alert immediately and persists after a page refresh — instead of the
// hard-coded default in FILES above. Root-cause fix: previously this engine
// only ever read FILES, so custom uploads were never heard on real events.
//
// Backend catalog key → this engine's FILES key.
const OVERRIDE_KEY_MAP = {
  panic:          "sos",
  booking:        "booking",
  booking_rider:  "booking_rider",
  booking_urgent: "booking_urgent",
  flag_critical:  "flag_critical",
  driver_online:  "driver",
  chat:           "chat",
  trip_initiated: "trip_initiated",
};
// engine-key → resolved absolute URL (only keys with an active custom upload).
const _overrides = {};

function _absUrl(u) {
  if (!u) return u;
  if (/^https?:\/\//i.test(u) || u.startsWith("data:") || u.startsWith("blob:")) return u;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return u.startsWith("/") ? `${base}${u}` : u;
}

// Active source for an engine key: uploaded override first, else the default.
function srcFor(key) {
  return _overrides[key] || FILES[key];
}

// Rebuild `_overrides` wholesale so a reset (custom_url removed) correctly
// falls back to the default. Returns true when the fetch succeeded.
async function loadSoundOverrides() {
  try {
    const { data } = await adminApi.get("/admin/system-sounds");
    Object.keys(_overrides).forEach((k) => delete _overrides[k]);
    (data?.items || []).forEach((r) => {
      const ek = OVERRIDE_KEY_MAP[r.key];
      if (ek && r.custom_url) _overrides[ek] = _absUrl(r.custom_url);
    });
    if (_overrides.chat) _overrides.chat_direct = _overrides.chat;  // chat_direct shares the chat tone
    return true;
  } catch {
    // 403 (non-admin dispatcher) or network — silently keep the defaults.
    return false;
  }
}

// v2.7.6+ · Mute preference is mirrored to localStorage so the engine can
// check it synchronously without an extra fetch on every event.
const MUTE_KEY = "rtg_admin_audio_muted";
// v2.7.65+E123 · Per-dispatcher volume preference (0..1). Persisted per
// browser so night-shift operators aren't blasted at 100%. Default 0.85
// matches the historical playOnce volume so existing behaviour is
// preserved when a browser has never set this key.
const VOLUME_KEY = "rtg_admin_audio_volume";
// v2.7.65+E124 · Independent booking-klaxon volume. Lets ops keep chat
// chimes quiet while the booking alarm stays loud. Defaults to 1.0
// (full blast) so the historical "you WILL hear a booking" behaviour
// is preserved when this key has never been set.
const BOOKING_VOLUME_KEY = "rtg_admin_audio_booking_volume";
// v2.8+E286 · Independent RIDER-APP booking klaxon volume, separate from the
// dispatch/call-in booking klaxon so ops can make self-service rider bookings
// louder (or quieter) than call-ins. Defaults 1.0 (full blast).
const BOOKING_RIDER_VOLUME_KEY = "rtg_admin_audio_booking_rider_volume";
const _clamp01 = (n) => Math.max(0, Math.min(1, n));
const _readNumKey = (key, fallback) => {
  try {
    const raw = window.localStorage?.getItem(key);
    if (raw === null || raw === undefined || raw === "") return fallback;
    const n = Number(raw);
    return Number.isNaN(n) ? fallback : _clamp01(n);
  } catch { return fallback; }
};
const _readVolume        = () => _readNumKey(VOLUME_KEY, 0.85);
const _readBookingVolume = () => _readNumKey(BOOKING_VOLUME_KEY, 1.0);
const _readBookingRiderVolume = () => _readNumKey(BOOKING_RIDER_VOLUME_KEY, 1.0);
let _muted = (typeof window !== "undefined" && window.localStorage?.getItem(MUTE_KEY) === "1");
let _volume        = (typeof window !== "undefined") ? _readVolume()        : 0.85;
let _bookingVolume = (typeof window !== "undefined") ? _readBookingVolume() : 1.0;
let _bookingRiderVolume = (typeof window !== "undefined") ? _readBookingRiderVolume() : 1.0;
if (typeof window !== "undefined") {
  window.addEventListener("rtg:audio_mute_changed", (e) => {
    _muted = !!e?.detail?.muted;
  });
  window.addEventListener("rtg:audio_volume_changed", (e) => {
    const v = Number(e?.detail?.volume);
    if (!Number.isNaN(v)) _volume = _clamp01(v);
  });
  window.addEventListener("rtg:audio_booking_volume_changed", (e) => {
    const v = Number(e?.detail?.volume);
    if (!Number.isNaN(v)) _bookingVolume = _clamp01(v);
  });
  window.addEventListener("rtg:audio_booking_rider_volume_changed", (e) => {
    const v = Number(e?.detail?.volume);
    if (!Number.isNaN(v)) _bookingRiderVolume = _clamp01(v);
  });
}


function playOnce(src) {
  if (_muted || _volume <= 0) return;
  // v2.7.65+E243 · Multi-window dedupe — only the audio-leader tab emits sound
  // so a dispatcher with several consoles open hears each notification once.
  if (!isAudioLeader()) return;
  try {
    const a = new Audio(src);
    a.volume = _volume;
    const p = a.play();
    // v2.7.65+E219 · Governed: standard chimes/sounds hard-stop at 30s. Track
    // only once playback actually starts so autoplay-blocked plays don't light
    // the indicator.
    audioManager.trackWhenPlaying(a, p, { kind: "standard" });
    if (p && typeof p.catch === "function") p.catch(() => { /* autoplay-blocked, silent */ });
  } catch { /* noop */ }
}


export default function useAudioAlerts() {
  // Shared SOS Audio element + active-id set (multiple panics share the loop).
  const sosRef = useRef(null);
  const activeSosIds = useRef(new Set());

  // v2.7.65+E85 · Shared BOOKING alarm loop + active-id set (multiple pending
  // bookings share one continuous alarm until each is individually or
  // globally acknowledged).
  const bookingRef = useRef(null);
  const activeBookingIds = useRef(new Set());

  // v2.8+E285 · Independent RIDER-APP booking alarm loop (source=rider_account).
  // Layers alongside the dispatch/call-in booking loop above so a dispatcher
  // hears BOTH tones concurrently until each is acknowledged.
  const bookingRiderRef = useRef(null);
  const activeBookingRiderIds = useRef(new Set());

  // v2.8+E295 · Independent URGENT/priority booking alarm loop (manual flag or
  // >5-min auto-escalation). Layers over BOTH standard booking loops and
  // follows the rider-app booking volume slider (user choice).
  const bookingUrgentRef = useRef(null);
  const activeBookingUrgentIds = useRef(new Set());

  // Ensure the loop elements exist exactly once.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    // v2.7.65+E243 · Join the same-origin audio-leader election so only ONE
    // admin window plays each notification when several are open.
    activateAudioLeader();
    const a = new Audio(srcFor("sos"));
    a.loop = true; a.volume = _volume; a.setAttribute("data-active-src", srcFor("sos"));
    sosRef.current = a;
    const b = new Audio(srcFor("booking"));
    b.loop = true; b.volume = _bookingVolume; b.setAttribute("data-active-src", srcFor("booking"));
    bookingRef.current = b;
    // v2.8+E285 · Second booking loop for rider-app bookings.
    const br = new Audio(srcFor("booking_rider"));
    br.loop = true; br.volume = _bookingRiderVolume; br.setAttribute("data-active-src", srcFor("booking_rider"));
    bookingRiderRef.current = br;
    // v2.8+E295 · Third booking loop for URGENT/priority bookings.
    const bu = new Audio(srcFor("booking_urgent"));
    bu.loop = true; bu.volume = _bookingRiderVolume; bu.setAttribute("data-active-src", srcFor("booking_urgent"));
    bookingUrgentRef.current = bu;

    // v2.8 · Point the loop elements at the uploaded custom tone (or back to
    // the default on reset). Called after the override table loads and again
    // whenever the Audio Test Panel reports a change.
    const applyLoopSrc = () => {
      const apply = (ref, key) => {
        const el = ref.current;
        if (!el) return;
        const want = srcFor(key);
        if ((el.getAttribute("data-active-src") || FILES[key]) === want) return;
        const wasPlaying = !el.paused;
        try { el.src = want; el.setAttribute("data-active-src", want); el.load(); } catch { /* noop */ }
        if (wasPlaying) { try { const p = el.play(); if (p && p.catch) p.catch(() => {}); } catch { /* noop */ } }
      };
      apply(sosRef, "sos");
      apply(bookingRef, "booking");
      apply(bookingRiderRef, "booking_rider");
      apply(bookingUrgentRef, "booking_urgent");
    };
    // Initial load (persists across refreshes) + live refresh on panel change.
    loadSoundOverrides().then((ok) => { if (ok) applyLoopSrc(); });
    // v2.7.65+E226 · Refresh instantly on a sound change from THIS tab (window
    // event) or ANY other open console tab (BroadcastChannel) — no wait.
    const unsubSounds = subscribeSoundChange(() => { loadSoundOverrides().then(() => applyLoopSrc()); });
    // v2.7.65+E225 · Cross-session propagation. The `rtg:system_sounds_updated`
    // event only reaches the SAME browser tab that uploaded. A dispatcher whose
    // console was already open when an admin changed a tone would otherwise keep
    // the stale (default) sound until a manual refresh. Poll the override table
    // every 30s so a new upload becomes the active live sound everywhere with
    // no extra steps. Lightweight GET; readable by any console user (E220).
    const overridePoll = setInterval(() => {
      loadSoundOverrides().then((ok) => { if (ok) applyLoopSrc(); });
    }, 30000);

    // Keep the SOS + chime loops in sync with master-volume changes and
    // the booking loop in sync with its own independent slider.
    const onVol = (e) => {
      const v = Number(e?.detail?.volume);
      if (Number.isNaN(v)) return;
      try { a.volume = _clamp01(v); } catch { /* noop */ }
    };
    const onBookingVol = (e) => {
      const v = Number(e?.detail?.volume);
      if (Number.isNaN(v)) return;
      try { b.volume = _clamp01(v); } catch { /* noop */ }
    };
    const onBookingRiderVol = (e) => {
      const v = Number(e?.detail?.volume);
      if (Number.isNaN(v)) return;
      try { br.volume = _clamp01(v); } catch { /* noop */ }
      // v2.8+E295 · Urgent booking loop follows the same rider-app slider.
      try { bu.volume = _clamp01(v); } catch { /* noop */ }
    };
    window.addEventListener("rtg:audio_volume_changed", onVol);
    window.addEventListener("rtg:audio_booking_volume_changed", onBookingVol);
    window.addEventListener("rtg:audio_booking_rider_volume_changed", onBookingRiderVol);
    return () => {
      try { unsubSounds(); } catch { /* noop */ }
      window.removeEventListener("rtg:audio_volume_changed", onVol);
      window.removeEventListener("rtg:audio_booking_volume_changed", onBookingVol);
      window.removeEventListener("rtg:audio_booking_rider_volume_changed", onBookingRiderVol);
      try { clearInterval(overridePoll); } catch { /* noop */ }
      try { a.pause(); } catch { /* noop */ }
      try { b.pause(); } catch { /* noop */ }
      try { br.pause(); } catch { /* noop */ }
      try { bu.pause(); } catch { /* noop */ }
      sosRef.current = null;
      bookingRef.current = null;
      bookingRiderRef.current = null;
      bookingUrgentRef.current = null;
      activeSosIds.current.clear();
      activeBookingIds.current.clear();
      activeBookingRiderIds.current.clear();
      activeBookingUrgentIds.current.clear();
    };
  }, []);

  // v2.7.65+E124 · Booking loops honour their own volume — everything
  // else uses the master `_volume`. Muted state kills every loop.
  // v2.8+E285 · `key` selects which tone/loop: "sos" | "booking" | "booking_rider".
  const startLoop = (ref, key = "sos") => {
    const el = ref.current;
    if (!el || _muted) return;
    const isBooking = key === "booking" || key === "booking_rider" || key === "booking_urgent";
    const level = key === "booking_rider" || key === "booking_urgent" ? _bookingRiderVolume
                : key === "booking" ? _bookingVolume
                : _volume;
    if (level <= 0) return;
    // v2.7.65+E243 · Only the audio-leader tab loops audibly; non-leader tabs
    // still track active ids (below) so a new leader can resume on handoff.
    if (!isAudioLeader()) return;
    if (el.paused) {
      // v2.7.65+E225 · Resolve the ACTIVE tone at PLAY TIME (the same way the
      // panel's Direct-play does) so a freshly-uploaded custom sound is always
      // used and a stale persistent `src` can never make the live alarm fall
      // back to the default. Only swaps when paused → never interrupts an
      // in-progress loop shared by multiple pending alerts.
      const want = srcFor(key);
      if ((el.getAttribute("data-active-src") || "") !== want) {
        try { el.src = want; el.setAttribute("data-active-src", want); el.load(); } catch { /* noop */ }
      }
      try { el.currentTime = 0; el.volume = level; } catch { /* noop */ }
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => {/* autoplay-blocked */});
      // v2.7.65+E219 / E285 · Governed cap: SOS = panic (45s), booking klaxons
      // (dispatch + rider) = 60s. Force-stopped at the cap regardless of the
      // `loop` flag; alert state is left untouched.
      audioManager.trackWhenPlaying(el, p, { kind: isBooking ? "booking" : "panic" });
    }
  };
  const stopLoopIfDone = (ref, set) => {
    if (set.size === 0 && ref.current) {
      try { ref.current.pause(); ref.current.currentTime = 0; } catch { /* noop */ }
      audioManager.untrackEl(ref.current);
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    // Panic (SOS)
    const onPanic = (e) => {
      const id = e?.detail?.id || e?.detail?.driver_id || `panic-${Date.now()}`;
      activeSosIds.current.add(id);
      startLoop(sosRef);
    };
    const onPanicAck = (e) => {
      const id = e?.detail?.id || e?.detail?.driver_id;
      if (id) activeSosIds.current.delete(id);
      else activeSosIds.current.clear();
      stopLoopIfDone(sosRef, activeSosIds.current);
    };
    // Public booking — new LOOP behaviour (E85). v2.8+E285 · a rider-app
    // booking (source=rider_account) routes to its OWN loop/tone so it layers
    // distinctly over a dispatch/call-in booking; everything else = dispatch.
    const onBooking = (e) => {
      const id = e?.detail?.trip_id || e?.detail?.id || `booking-${Date.now()}`;
      const isRider = String(e?.detail?.source || "").toLowerCase() === "rider_account";
      if (isRider) {
        activeBookingRiderIds.current.add(id);
        startLoop(bookingRiderRef, "booking_rider");
      } else {
        activeBookingIds.current.add(id);
        startLoop(bookingRef, "booking");
      }
    };
    const onBookingAck = (e) => {
      const id = e?.detail?.trip_id || e?.detail?.id;
      if (id) {
        activeBookingIds.current.delete(id);
        activeBookingRiderIds.current.delete(id);
      } else {
        activeBookingIds.current.clear();
        activeBookingRiderIds.current.clear();
      }
      stopLoopIfDone(bookingRef, activeBookingIds.current);
      stopLoopIfDone(bookingRiderRef, activeBookingRiderIds.current);
    };
    // v2.8+E295 · URGENT/priority booking — its OWN loop/tone layered over the
    // standard booking alarms. Fired by the Ride Coordinator board when a ride
    // is flagged urgent or auto-escalates past 5 min; acknowledged (per id) the
    // moment the ride is assigned / de-escalated / leaves the board.
    const onBookingUrgent = (e) => {
      const id = e?.detail?.id || e?.detail?.trip_id || `urgent-${Date.now()}`;
      activeBookingUrgentIds.current.add(id);
      startLoop(bookingUrgentRef, "booking_urgent");
    };
    const onBookingUrgentAck = (e) => {
      const id = e?.detail?.id || e?.detail?.trip_id;
      if (id) activeBookingUrgentIds.current.delete(id);
      else activeBookingUrgentIds.current.clear();
      stopLoopIfDone(bookingUrgentRef, activeBookingUrgentIds.current);
    };
    // Master ACK — silences EVERYTHING (SOS + all three booking loops).
    const onAckAll = () => {
      activeSosIds.current.clear();
      activeBookingIds.current.clear();
      activeBookingRiderIds.current.clear();
      activeBookingUrgentIds.current.clear();
      stopLoopIfDone(sosRef, activeSosIds.current);
      stopLoopIfDone(bookingRef, activeBookingIds.current);
      stopLoopIfDone(bookingRiderRef, activeBookingRiderIds.current);
      stopLoopIfDone(bookingUrgentRef, activeBookingUrgentIds.current);
    };
    const onDriverOnline = () => playOnce(srcFor("driver"));
    // v2.8+E322 · Critical rider-flag chime (one-shot). Fired by the Ride
    // Coordinator board when a ride with a critical flag lands in the queue.
    const onFlagCritical = () => playOnce(srcFor("flag_critical"));
    const onChat = () => playOnce(srcFor("chat"));
    const onChatDirect = () => playOnce(srcFor("chat_direct"));   // E108
    const onTripInitiated = () => playOnce(srcFor("trip_initiated"));
    // v2.7.65+E120 · new critical triggers
    const onTripCompleted = () => playOnce(srcFor("trip_completed"));
    const onTicketPipeline = () => playOnce(srcFor("ticket_pipeline"));

    // v2.7.65+E243 · On audio-leadership handoff, the new leader resumes any
    // still-active loops (SOS/booking) so a closed leader never leaves alerts
    // silent; a demoted tab pauses its loops (keeping active ids for resume).
    const offLeader = onLeadershipChange((leader) => {
      if (leader) {
        if (activeSosIds.current.size > 0) startLoop(sosRef, "sos");
        if (activeBookingIds.current.size > 0) startLoop(bookingRef, "booking");
        if (activeBookingRiderIds.current.size > 0) startLoop(bookingRiderRef, "booking_rider");
        if (activeBookingUrgentIds.current.size > 0) startLoop(bookingUrgentRef, "booking_urgent");
      } else {
        try { sosRef.current?.pause(); } catch { /* noop */ }
        try { bookingRef.current?.pause(); } catch { /* noop */ }
        try { bookingRiderRef.current?.pause(); } catch { /* noop */ }
        try { bookingUrgentRef.current?.pause(); } catch { /* noop */ }
      }
    });

    window.addEventListener("rtg:panic_alert", onPanic);
    window.addEventListener("rtg:panic_acknowledged", onPanicAck);
    window.addEventListener("rtg:public_booking", onBooking);
    window.addEventListener("rtg:public_booking_acknowledged", onBookingAck);
    window.addEventListener("rtg:booking_urgent", onBookingUrgent);
    window.addEventListener("rtg:booking_urgent_acknowledged", onBookingUrgentAck);
    window.addEventListener("rtg:acknowledge_all_alarms", onAckAll);
    // v2.7.65+E219 · Master audio-stop indicator also acknowledges the loops so
    // a hard-stopped loop element can't re-arm (audio-only; alert UI untouched).
    window.addEventListener("rtg:audio_master_stopped", onAckAll);
    window.addEventListener("rtg:driver_online", onDriverOnline);
    window.addEventListener("rtg:flag_critical", onFlagCritical);
    window.addEventListener("rtg:chat_message", onChat);
    window.addEventListener("rtg:chat_message_direct", onChatDirect);   // E108
    window.addEventListener("rtg:trip_initiated", onTripInitiated);
    window.addEventListener("rtg:trip_completed", onTripCompleted);     // E120
    window.addEventListener("rtg:ticket_pipeline", onTicketPipeline);   // E120
    return () => {
      try { offLeader(); } catch { /* noop */ }
      window.removeEventListener("rtg:panic_alert", onPanic);
      window.removeEventListener("rtg:panic_acknowledged", onPanicAck);
      window.removeEventListener("rtg:public_booking", onBooking);
      window.removeEventListener("rtg:public_booking_acknowledged", onBookingAck);
      window.removeEventListener("rtg:booking_urgent", onBookingUrgent);
      window.removeEventListener("rtg:booking_urgent_acknowledged", onBookingUrgentAck);
      window.removeEventListener("rtg:acknowledge_all_alarms", onAckAll);
      window.removeEventListener("rtg:audio_master_stopped", onAckAll);
      window.removeEventListener("rtg:driver_online", onDriverOnline);
      window.removeEventListener("rtg:flag_critical", onFlagCritical);
      window.removeEventListener("rtg:chat_message", onChat);
      window.removeEventListener("rtg:chat_message_direct", onChatDirect);
      window.removeEventListener("rtg:trip_initiated", onTripInitiated);
      window.removeEventListener("rtg:trip_completed", onTripCompleted);
      window.removeEventListener("rtg:ticket_pipeline", onTicketPipeline);
    };
  }, []);
}
